import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Spin,
  message,
  Table,
  Tag,
  Modal,
  Alert,
  Typography,
  Tooltip,
  Empty,
  Result
} from 'antd';
import {
  CreditCardOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  StarOutlined,
  StarFilled,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/admin/governanceService';
import { getPaymentMethodsConfig } from '@config/governance/paymentMethods';
import ListPageHeader from '@shared/ListPageHeader';

const { Text } = Typography;

// Outcome dispatch table for 3D verification return.
// Keys must match values produced by PayuniController#compute_binding_outcome.
const BINDING_OUTCOMES = {
  success: { severity: 'success', titleKey: '3d_verification_complete' },
  duplicate: { severity: 'info', titleKey: 'outcomes.duplicate.title', messageKey: 'outcomes.duplicate.message' },
  verification_failed: { severity: 'error', titleKey: 'add_card_failed', messageKey: 'verification_failed_desc' },
  failed: { severity: 'error', titleKey: 'add_card_failed', messageKey: 'card_binding_failed' }
};

const CARD_BRAND_COLORS = {
  visa: '#1a1f71',
  mastercard: '#eb001b',
  jcb: '#0066cc',
  amex: '#006fcf',
  unknown: '#666666'
};

const PaymentMethods = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentGateway, setPaymentGateway] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Card binding modal state
  const [addCardModalVisible, setAddCardModalVisible] = useState(false);
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [bindingStatus, setBindingStatus] = useState(null);
  const [bindingError, setBindingError] = useState(null);
  const [currentSdkToken, setCurrentSdkToken] = useState(null);
  const payuniContainerRef = useRef(null);
  const uniPaymentRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [methodsRes, profileRes] = await Promise.all([
        governanceService.getPaymentMethods(),
        governanceService.getBillingProfile()
      ]);

      if (methodsRes.success) {
        setPaymentMethods(methodsRes.data.payment_methods || []);
      } else {
        message.error(methodsRes.message || t('common.error.fetch_failed'));
      }

      // Detect payment gateway from billing profile (e.g. 'payuni', 'stripe')
      if (profileRes.success) {
        const profile = profileRes.data.billing_profile;
        const gateway = profile?.payment_gateway;
        setPaymentGateway(gateway);
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle return from 3D Secure verification.
  // See operator/Governance/PaymentMethods.jsx for design notes — same dispatch.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('binding_complete') !== 'true') return;

    const outcome = urlParams.get('outcome') || 'failed';
    sessionStorage.removeItem('payuni_binding_in_progress');
    window.history.replaceState({}, document.title, window.location.pathname);

    const meta = BINDING_OUTCOMES[outcome] || BINDING_OUTCOMES.failed;
    const ns = 'admin.ai_center.payment_methods.';
    const title = t(ns + meta.titleKey);
    const content = meta.messageKey ? t(ns + meta.messageKey) : undefined;

    if (meta.severity === 'success') {
      message.success(title);
    } else {
      Modal[meta.severity]({ title, content });
    }

    fetchData();
  }, [fetchData, t]);

  // Load PayUNi SDK script dynamically
  const loadPayuniSdk = useCallback((sdkUrl) => {
    return new Promise((resolve, reject) => {
      if (window.UNiPayment || window.UniPayment) {
        resolve();
        return;
      }

      const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
      if (existingScript) {
        setTimeout(() => resolve(), 500);
        return;
      }

      const script = document.createElement('script');
      script.src = sdkUrl;
      script.async = true;
      script.onload = () => {
        setTimeout(() => resolve(), 300);
      };
      script.onerror = () => {
        reject(new Error('Failed to load PayUNi SDK'));
      };
      document.head.appendChild(script);
    });
  }, []);

  // Initialize PayUNi UNi Embed
  const initializePayuni = useCallback(async () => {
    setAddCardLoading(true);
    setBindingStatus(null);
    setBindingError(null);

    try {
      const response = await governanceService.getSdkToken(window.location.href, 1);

      if (!response.success) {
        throw new Error(response.message || 'Failed to get SDK token');
      }

      const { sdk_token, sdk_url } = response.data;
      setCurrentSdkToken(sdk_token);

      await loadPayuniSdk(sdk_url);
      setSdkLoaded(true);

      await new Promise(resolve => setTimeout(resolve, 200));

      const PayUNiSDK = window.UNiPayment || window.UniPayment || window.UPi || window.PAYUNi;

      if (!PayUNiSDK) {
        throw new Error('PayUNi SDK not found.');
      }

      const containerId = 'payuni-container';

      if (typeof PayUNiSDK === 'function') {
        uniPaymentRef.current = new PayUNiSDK({
          token: sdk_token,
          container: containerId,
          onReady: () => setAddCardLoading(false),
          onSuccess: (result) => {
            setBindingStatus('success');
            setTimeout(() => {
              fetchData();
              setAddCardModalVisible(false);
              message.success(t('admin.ai_center.payment_methods.add_card_success'));
            }, 2000);
          },
          onError: (error) => {
            setBindingStatus('error');
            setBindingError(error.message || 'Card binding failed');
          },
          onCancel: () => setAddCardModalVisible(false)
        });
      } else if (typeof PayUNiSDK.createSession === 'function') {
        const isSandbox = sdk_url?.includes('sandbox');
        const sdkOptions = {
          env: isSandbox ? 'S' : 'P',
          useInst: false,
          elements: {
            CardNo: 'payuni-card-no',
            CardExp: 'payuni-card-exp',
            CardCvc: 'payuni-card-cvc',
            CardTokenType: 'payuni-token-type'
          },
          style: {
            color: '#000000',
            errorColor: '#FF0000',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '24px'
          }
        };

        uniPaymentRef.current = window.UniPayment.createSession(sdk_token, sdkOptions);

        uniPaymentRef.current.onUpdate((update) => {
          const { event, data } = update;
          if (event === 'useTokenType' && data) {
            const tokenTypeArea = document.getElementById('payuni-token-type-area');
            if (tokenTypeArea) tokenTypeArea.style.display = 'flex';
            const tokenTypeText = document.getElementById('payuni-token-type-text');
            if (tokenTypeText && data.tokenTypeText) tokenTypeText.innerHTML = data.tokenTypeText;
          }
        });

        try {
          await uniPaymentRef.current.start();
          setAddCardLoading(false);
        } catch (startError) {
          throw new Error(startError.message || 'Failed to initialize payment form');
        }
      } else if (typeof PayUNiSDK.init === 'function') {
        uniPaymentRef.current = PayUNiSDK.init({
          token: sdk_token,
          container: containerId,
          onReady: () => setAddCardLoading(false),
          onSuccess: (result) => {
            setBindingStatus('success');
            setTimeout(() => {
              fetchData();
              setAddCardModalVisible(false);
              message.success(t('admin.ai_center.payment_methods.add_card_success'));
            }, 2000);
          },
          onError: (error) => {
            setBindingStatus('error');
            setBindingError(error.message || 'Card binding failed');
          }
        });
      } else {
        throw new Error('Unknown PayUNi SDK API pattern');
      }
    } catch (error) {
      console.error('[PayUNi] Initialization error:', error);
      setAddCardLoading(false);
      setBindingStatus('error');
      setBindingError(error.message);
    }
  }, [fetchData, loadPayuniSdk, t]);

  const handleAddCard = () => {
    setAddCardModalVisible(true);
    setSdkLoaded(false);
    setBindingStatus(null);
    setBindingError(null);

    // Gateway-driven SDK initialization
    // Currently supported: 'payuni'. Extend here for 'stripe', etc.
    if (paymentGateway === 'payuni' || !paymentGateway) {
      setTimeout(() => initializePayuni(), 300);
    } else {
      // Future: initializeStripe(), initializeOther(), etc.
      console.warn(`Unsupported payment gateway: ${paymentGateway}`);
      setBindingStatus('error');
      setBindingError(`Payment gateway "${paymentGateway}" is not yet supported`);
    }
  };

  const handleAddCardModalClose = () => {
    setAddCardModalVisible(false);
    setAddCardLoading(false);
    setSdkLoaded(false);
    setBindingStatus(null);
    setBindingError(null);
    setCurrentSdkToken(null);
    if (uniPaymentRef.current) uniPaymentRef.current = null;
  };

  const handleSubmitCard = async () => {
    if (!uniPaymentRef.current || !currentSdkToken) {
      message.error(t('admin.ai_center.payment_methods.sdk_not_initialized'));
      return;
    }

    try {
      setBindingStatus('processing');
      const tradeResult = await uniPaymentRef.current.getTradeResult();

      const bindResponse = await governanceService.bindCard(currentSdkToken, true);

      if (bindResponse.success) {
        if (bindResponse.data?.requires_3d && bindResponse.data?.redirect_url) {
          message.info(t('admin.ai_center.payment_methods.redirecting_to_3d'));
          sessionStorage.setItem('payuni_binding_in_progress', 'true');
          window.location.href = bindResponse.data.redirect_url;
          return;
        }

        setBindingStatus('success');
        message.success(t('admin.ai_center.payment_methods.add_card_success'));
        setTimeout(() => {
          fetchData();
          setAddCardModalVisible(false);
        }, 2000);
      } else {
        throw new Error(bindResponse.error || bindResponse.message || 'Card binding failed');
      }
    } catch (error) {
      console.error('[PayUNi] Card binding failed:', error);
      setBindingStatus('error');
      setBindingError(error?.message || t('admin.ai_center.payment_methods.card_binding_failed'));
    }
  };

  const handleDelete = async () => {
    if (!selectedMethod) return;

    try {
      setDeleting(true);
      const response = await governanceService.removePaymentMethod(selectedMethod.id);

      if (response.success) {
        message.success(t('admin.ai_center.payment_methods.delete_success'));
        setDeleteModalVisible(false);
        setSelectedMethod(null);
        fetchData();
      } else {
        message.error(response.message || t('common.error.delete_failed'));
      }
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      message.error(t('common.error.delete_failed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      setSettingDefault(true);
      const response = await governanceService.setDefaultPaymentMethod(methodId);

      if (response.success) {
        message.success(t('admin.ai_center.payment_methods.set_default_success'));
        fetchData();
      } else {
        message.error(response.message || t('common.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      message.error(t('common.error.update_failed'));
    } finally {
      setSettingDefault(false);
    }
  };

  const showDeleteConfirm = (method) => {
    setSelectedMethod(method);
    setDeleteModalVisible(true);
  };

  const columns = [
    {
      title: t('admin.ai_center.payment_methods.type'),
      dataIndex: 'method_type',
      key: 'method_type',
      render: (type) => (
        <Space>
          <CreditCardOutlined />
          {t(`admin.ai_center.payment_methods.types.${type}`)}
        </Space>
      )
    },
    {
      title: t('admin.ai_center.payment_methods.card_info'),
      key: 'card_info',
      render: (_, record) => (
        <Space>
          <Tag color={CARD_BRAND_COLORS[record.card_brand] || CARD_BRAND_COLORS.unknown}>
            {record.card_brand?.toUpperCase() || 'CARD'}
          </Tag>
          <Text>**** **** **** {record.card_last_four}</Text>
        </Space>
      )
    },
    {
      title: t('admin.ai_center.payment_methods.expiry'),
      key: 'expiry',
      render: (_, record) => {
        if (!record.card_exp_month || !record.card_exp_year) return '-';
        const expiry = `${String(record.card_exp_month).padStart(2, '0')}/${record.card_exp_year}`;

        if (record.card_expired) {
          return (
            <Tag color="red" icon={<ExclamationCircleOutlined />}>
              {expiry} ({t('admin.ai_center.payment_methods.expired')})
            </Tag>
          );
        }

        if (record.card_expiring_soon) {
          return (
            <Tag color="orange" icon={<ExclamationCircleOutlined />}>
              {expiry} ({t('admin.ai_center.payment_methods.expiring_soon')})
            </Tag>
          );
        }

        return expiry;
      }
    },
    {
      title: t('admin.ai_center.payment_methods.default'),
      dataIndex: 'is_default',
      key: 'is_default',
      align: 'center',
      render: (isDefault, record) => (
        isDefault ? (
          <Tag color="green" icon={<StarFilled />}>
            {t('admin.ai_center.payment_methods.default_label')}
          </Tag>
        ) : (
          <Tooltip title={t('admin.ai_center.payment_methods.set_as_default')}>
            <Button
              type="link"
              icon={<StarOutlined />}
              onClick={() => handleSetDefault(record.id)}
              loading={settingDefault}
            />
          </Tooltip>
        )
      )
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Tooltip title={t('common.button.delete')}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record)}
          />
        </Tooltip>
      )
    }
  ];

  const renderAddCardContent = () => {
    if (bindingStatus === 'success') {
      return (
        <Result
          status="success"
          icon={<CheckCircleOutlined />}
          title={t('admin.ai_center.payment_methods.add_card_success')}
          subTitle={t('admin.ai_center.payment_methods.card_added_desc')}
        />
      );
    }

    if (bindingStatus === 'error') {
      return (
        <Result
          status="error"
          icon={<CloseCircleOutlined />}
          title={t('admin.ai_center.payment_methods.add_card_failed')}
          subTitle={bindingError}
          extra={[
            <Button key="retry" type="primary" onClick={initializePayuni}>
              {t('common.button.retry')}
            </Button>,
            <Button key="cancel" onClick={handleAddCardModalClose}>
              {t('common.button.cancel')}
            </Button>
          ]}
        />
      );
    }

    return (
      <>
        <Alert
          message={t('admin.ai_center.payment_methods.card_binding_notice')}
          description={t('admin.ai_center.payment_methods.card_binding_desc')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {addCardLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <Text style={{ marginLeft: 12 }}>{t('admin.ai_center.payment_methods.loading_payment_form')}</Text>
          </div>
        )}

        <div
          ref={payuniContainerRef}
          id="payuni-container"
          style={{ display: addCardLoading ? 'none' : 'block' }}
        >
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('admin.ai_center.payment_methods.card_number')}
            </Text>
            <div
              id="payuni-card-no"
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '8px 12px',
                minHeight: 40,
                backgroundColor: '#fff'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {t('admin.ai_center.payment_methods.card_expiry')}
              </Text>
              <div
                id="payuni-card-exp"
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: '8px 12px',
                  minHeight: 40,
                  backgroundColor: '#fff'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {t('admin.ai_center.payment_methods.card_cvc')}
              </Text>
              <div
                id="payuni-card-cvc"
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: '8px 12px',
                  minHeight: 40,
                  backgroundColor: '#fff'
                }}
              />
            </div>
          </div>

          <div
            id="payuni-token-type-area"
            style={{ display: 'none', alignItems: 'center', marginBottom: 16 }}
          >
            <div id="payuni-token-type" style={{ display: 'flex', alignItems: 'center' }} />
            <label
              id="payuni-token-type-text"
              htmlFor="type-checkbox"
              style={{ marginLeft: 8, color: '#666', fontSize: 12 }}
            />
          </div>

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Button
              type="primary"
              size="large"
              icon={<CreditCardOutlined />}
              onClick={handleSubmitCard}
              loading={bindingStatus === 'processing'}
              style={{ minWidth: 200 }}
            >
              {t('admin.ai_center.payment_methods.bind_card')}
            </Button>
          </div>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <ListPageHeader items={['ai_center', 'ai_center.payment_methods']} basePath="/admin" />

      <div style={{ padding: '0 24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card
            title={
              <Space>
                <CreditCardOutlined />
                {t('admin.ai_center.payment_methods.credit_cards')}
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddCard}
              >
                {t('admin.ai_center.payment_methods.add_card')}
              </Button>
            }
          >
            {paymentMethods.length > 0 ? (
              <Table
                dataSource={paymentMethods}
                columns={columns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('admin.ai_center.payment_methods.no_cards')}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCard}
                >
                  {t('admin.ai_center.payment_methods.add_first_card')}
                </Button>
              </Empty>
            )}
          </Card>
        </Space>
      </div>

      {/* Add Card Modal */}
      <Modal
        title={
          <Space>
            <CreditCardOutlined />
            {t('admin.ai_center.payment_methods.add_card')}
          </Space>
        }
        open={addCardModalVisible}
        onCancel={handleAddCardModalClose}
        footer={bindingStatus ? null : [
          <Button key="cancel" onClick={handleAddCardModalClose}>
            {t('common.button.cancel')}
          </Button>
        ]}
        width={600}
        destroyOnHidden
      >
        {renderAddCardContent()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title={t('admin.ai_center.payment_methods.delete_confirm_title')}
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedMethod(null);
        }}
        confirmLoading={deleting}
        okText={t('common.button.delete')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.button.cancel')}
      >
        {selectedMethod && (
          <p>
            {t('admin.ai_center.payment_methods.delete_confirm_desc', {
              card: `**** ${selectedMethod.card_last_four}`
            })}
          </p>
        )}
        <Alert
          message={t('admin.ai_center.payment_methods.delete_warning')}
          type="warning"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default PaymentMethods;
